import {
  ACROSS_TOKEN_BY_CHAIN,
  INK_NATIVE_USDC,
  INK_USDT0,
  isLegacyEthereumUsdtAcrossInput,
  normalizeAcrossSpenderAddress,
} from '../config/across';
import { ERC20_ABI } from '../config/oft';

export interface AcrossAllowanceDeposit {
  inputAmount: bigint | string;
  inputToken: `0x${string}`;
  isNative?: boolean;
  originChainId: number;
  spokePoolAddress: `0x${string}`;
}

type AcrossAllowancePublicClient = {
  readContract: (params: {
    address: `0x${string}`;
    abi: typeof ERC20_ABI;
    functionName: 'allowance';
    args: [`0x${string}`, `0x${string}`];
  }) => Promise<bigint>;
  waitForTransactionReceipt: (params: { hash: `0x${string}` }) => Promise<unknown>;
};

type AcrossApproveTransactionSender = (params: {
  tokenAddress: `0x${string}`;
  spender: `0x${string}`;
  amount: bigint;
  chainId: number;
  description: string;
}) => Promise<`0x${string}`>;

type AcrossAllowanceProgressCallback = (
  step: string,
  status: string,
  txHash?: `0x${string}`,
) => void;

export async function ensureAcrossDepositAllowance({
  deposit,
  accountAddress,
  publicClient,
  sendApproveTransaction,
  onProgress,
}: {
  deposit: AcrossAllowanceDeposit | undefined;
  accountAddress: `0x${string}` | undefined;
  publicClient: AcrossAllowancePublicClient;
  sendApproveTransaction: AcrossApproveTransactionSender;
  onProgress?: AcrossAllowanceProgressCallback;
}): Promise<boolean> {
  if (!deposit || deposit.isNative) {
    return false;
  }
  if (!accountAddress) {
    throw new Error('Wallet account is required for Across approval');
  }

  const spender = normalizeAcrossSpenderAddress(deposit.spokePoolAddress);
  const approvalAmount = BigInt(deposit.inputAmount.toString());
  const currentAllowance = await publicClient.readContract({
    address: deposit.inputToken,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: [accountAddress, spender],
  });

  if (currentAllowance >= approvalAmount) {
    return true;
  }

  if (currentAllowance > BigInt("0") && isLegacyEthereumUsdtAcrossInput(deposit)) {
    onProgress?.('approve', 'txPending');
    const resetTxHash = await sendApproveTransaction({
      tokenAddress: deposit.inputToken,
      spender,
      amount: BigInt("0"),
      chainId: deposit.originChainId,
      description: 'Reset USDT approval for Across',
    });
    await publicClient.waitForTransactionReceipt({ hash: resetTxHash });
  }

  onProgress?.('approve', 'txPending');
  let approveTxHash: `0x${string}` | undefined;
  try {
    approveTxHash = await sendApproveTransaction({
      tokenAddress: deposit.inputToken,
      spender,
      amount: approvalAmount,
      chainId: deposit.originChainId,
      description: `Approve ${getAcrossApprovalTokenSymbol(deposit)} for Across`,
    });
  } catch (err) {
    const didRecoverAllowance = await waitForAcrossDepositAllowance({
      publicClient,
      tokenAddress: deposit.inputToken,
      accountAddress,
      spender,
      approvalAmount,
      attempts: 12,
      delayMs: 2500,
      throwOnFailure: false,
    });
    if (didRecoverAllowance) {
      onProgress?.('approve', 'success');
      return true;
    }
    throw err;
  }
  await publicClient.waitForTransactionReceipt({ hash: approveTxHash });
  await waitForAcrossDepositAllowance({
    publicClient,
    tokenAddress: deposit.inputToken,
    accountAddress,
    spender,
    approvalAmount,
  });
  onProgress?.('approve', 'success', approveTxHash);

  return true;
}

async function waitForAcrossDepositAllowance({
  publicClient,
  tokenAddress,
  accountAddress,
  spender,
  approvalAmount,
  attempts = 6,
  delayMs = 500,
  throwOnFailure = true,
}: {
  publicClient: AcrossAllowancePublicClient;
  tokenAddress: `0x${string}`;
  accountAddress: `0x${string}`;
  spender: `0x${string}`;
  approvalAmount: bigint;
  attempts?: number;
  delayMs?: number;
  throwOnFailure?: boolean;
}): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const allowance = await publicClient.readContract({
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [accountAddress, spender],
    });
    if (allowance >= approvalAmount) {
      return true;
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  if (!throwOnFailure) {
    return false;
  }
  throw new Error('Across allowance approval was not readable after confirmation');
}

function getAcrossApprovalTokenSymbol({
  originChainId,
  inputToken,
}: {
  originChainId: number;
  inputToken: string;
}): string {
  const normalizedInput = inputToken.toLowerCase();
  if (originChainId === 57073 && normalizedInput === INK_USDT0.toLowerCase()) {
    return 'USDT0';
  }
  if (originChainId === 57073 && normalizedInput === INK_NATIVE_USDC.toLowerCase()) {
    return 'USDC';
  }

  const tokenSymbols = ACROSS_TOKEN_BY_CHAIN[originChainId];
  const matchingSymbol = Object.entries(tokenSymbols ?? {}).find(
    ([, address]) => address?.toLowerCase() === normalizedInput,
  )?.[0];

  return matchingSymbol ?? 'token';
}
